async function initPdfJs() {
  const pdfjsLib = await import('pdfjs-dist');
  if (
    typeof window !== 'undefined' &&
    !pdfjsLib.GlobalWorkerOptions.workerSrc
  ) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
  }
  return pdfjsLib;
}

async function loadPdf(file: File) {
  const pdfjsLib = await initPdfJs();
  const arrayBuffer = await file.arrayBuffer();
  return pdfjsLib.getDocument({ data: arrayBuffer }).promise;
}

/**
 * Converts the first page of a PDF to a base64 JPEG image.
 */
export async function pdfFirstPageToBase64(file: File): Promise<string> {
  const pdf = await loadPdf(file);
  const page = await pdf.getPage(1);
  const viewport = page.getViewport({ scale: 2 });

  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext('2d')!;

  await (page.render({
    canvasContext: ctx,
    canvas,
    viewport,
  }) as unknown as Promise<void>);
  return canvas.toDataURL('image/jpeg', 0.9);
}

/**
 * Converts up to `maxPages` pages of a PDF to base64 JPEG images.
 */
export async function pdfPagesToBase64(
  file: File,
  maxPages = 3
): Promise<string[]> {
  const pdf = await loadPdf(file);
  const count = Math.min(pdf.numPages, maxPages);
  const pages: string[] = [];

  for (let i = 1; i <= count; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d')!;
    await (page.render({
      canvasContext: ctx,
      canvas,
      viewport,
    }) as unknown as Promise<void>);
    pages.push(canvas.toDataURL('image/jpeg', 0.9));
  }

  return pages;
}

/**
 * Extracts text from a PDF preserving two-column layout with " | " separators.
 */
export async function pdfToText(file: File, maxPages = 5): Promise<string> {
  const pdf = await loadPdf(file);
  const count = Math.min(pdf.numPages, maxPages);
  const parts: string[] = [];

  for (let i = 1; i <= count; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const { width: pageW, height: pageH } = page.getViewport({ scale: 1 });

    const items = (content.items as any[])
      .filter((it) => it.str?.trim())
      .map((it) => ({
        str: it.str as string,
        x: it.transform?.[4] ?? 0,
        y: pageH - (it.transform?.[5] ?? 0),
      }));

    if (!items.length) continue;

    // Group by Y position (5px tolerance)
    const lines = new Map<number, typeof items>();
    for (const item of items) {
      const key = Math.round(item.y / 5) * 5;
      if (!lines.has(key)) lines.set(key, []);
      lines.get(key)!.push(item);
    }

    const midX = pageW / 2;
    const text = Array.from(lines.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([, lineItems]) => {
        lineItems.sort((a, b) => a.x - b.x);
        const left = lineItems.filter((i) => i.x < midX - 20);
        const right = lineItems.filter((i) => i.x >= midX - 20);

        if (left.length && right.length) {
          return `${left.map((i) => i.str).join(' ')}  |  ${right.map((i) => i.str).join(' ')}`;
        }
        return lineItems.map((i) => i.str).join(' ');
      })
      .join('\n');

    parts.push(count > 1 ? `--- Page ${i} ---\n${text}` : text);
  }

  return parts.join('\n\n');
}
